import { zodResolver } from '@hookform/resolvers/zod';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Save from '@mui/icons-material/Save';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FloatingSaveButton } from '@/components/floating-save-button';

import AuthGuard from '@/components/auth-guard';
import { BasicIdentitySection } from '@/components/location-form/basic-identity';
import { CustomFieldsSection } from '@/components/location-form/custom-fields-section';
import { Page } from '@/components/page';
import {
  ImageManager,
  LocationFormSchema,
  LocationFormValues,
  useLocationAccessLayer,
} from '@/features/locations';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';

export const Route = createFileRoute('/locations/new')({
  component: NewLocationPage,
});

function NewLocationForm() {
  const navigate = useNavigate();
  const { novel, getNewId } = useNovelRepository();
  const novelId = novel?.id;
  const { toast } = useToast();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();

  const locationId = useMemo(() => getNewId(), [getNewId]);

  const {
    createLocation: createLocationLayer,
    location: combinedData,
    locations,
    sections: locationSettings,
  } = useLocationAccessLayer(locationId);


  const { processImages, isProcessing } = useImageProcessor();
  const [isSaving, setIsSaving] = useState(false);

  if (!novel.id) {
    navigate({ to: '/locations' });
    return null;
  }


  const methods = useForm<LocationFormValues>({
    resolver: zodResolver(LocationFormSchema),
    defaultValues: {
      name: '',
      type: '',
      shortDescription: '',
      region: '',
      parentId: null,
      images: [],
      mapData: null,
      pendingFiles: [],
      primaryImageId: null,
      audit: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    },
  });

  // Sync images and primary UUID from background updates (e.g. AI generation)
  useEffect(() => {
    if (combinedData?.images && JSON.stringify(combinedData.images) !== JSON.stringify(methods.getValues('images'))) {
      methods.setValue('images', combinedData.images as LocationFormValues['images'], { shouldDirty: true });
    }
    if (combinedData?.primaryImageId !== undefined && combinedData.primaryImageId !== methods.getValues('primaryImageId')) {
      methods.setValue('primaryImageId', combinedData.primaryImageId, { shouldDirty: true });
    }
  }, [combinedData?.images, combinedData?.primaryImageId, methods]);

  const { watch, setValue } = methods;
  const watchedImages = watch('images');
  const watchedPendingFiles = watch('pendingFiles');
  const watchedPrimaryImageId = watch('primaryImageId');
  const watchedName = watch('name');
  const watchedParentId = watch('parentId');

  const selectedParent = useMemo(
    () => locations.find((location) => location.id === watchedParentId) ?? null,
    [locations, watchedParentId],
  );
  const previewLocationName = watchedName?.trim()
    || t('entity_form.locations.hierarchy_new_location');

  const onSubmit = async (values: LocationFormValues) => {
    if (!novelId) return;

    setIsSaving(true);
    try {
      const { pendingFiles, ...locationData } = {
        ...values,
        parentId: selectedParent?.id ?? null,
      };
      const { defined: coreData, details } = splitBySchemaShape(LocationFormSchema, locationData);

      let imagesUpdatedArray = [...(coreData.images || [])];

      // 1. Process images via shared hook
      if (pendingFiles && pendingFiles.length > 0) {
        const processedResults = await processImages(
          pendingFiles,
          novelId,
          `locations/${locationId}`,
          novel.ownerId
        );
        imagesUpdatedArray = [...imagesUpdatedArray, ...processedResults];
      }

      const primaryImageId = coreData.primaryImageId ?? imagesUpdatedArray[0]?.id ?? null;
      const mainImage = imagesUpdatedArray.find(image => image.id === primaryImageId);
      const mainImageUrl = mainImage?.profile?.url || mainImage?.original?.url || '';
      const mainImagePath = mainImage?.profile?.path || mainImage?.original?.path || '';

      const safeDetails = Object.fromEntries(
        Object.entries(details).map(([k, v]) => [k, String(v)])
      );

      // 2. Create via Access Layer
      await createLocationLayer(locationId, {
        ...coreData,
        id: locationId,
        details: safeDetails,
        images: imagesUpdatedArray,
        primaryImageId,
        mainImageUrl,
        mainImagePath,
      });

      toast({
        title: t('entity_form.locations.create_success_title'),
        description: t('entity_form.locations.create_success_desc', { name: values.name }),
      });

      navigate({ to: '/locations' });
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: t('entity_form.common.save_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onInvalid = (errors: FieldErrors<LocationFormValues>) => {
    const errorFields = Object.keys(errors).map(key => {
      const fieldName = String(key);
      return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    });

    toast({
      title: t('entity_form.common.validation_failed_title'),
      description: t('entity_form.common.validation_failed_desc', { fields: errorFields.join(', ') }),
      variant: 'destructive',
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
        <Page
          title={t('entity_form.locations.create_title')}
          description={t('entity_form.locations.create_desc')}
          breadcrumbs={[
            { label: t('entity_list.locations.title'), href: '/locations' },
            { label: t('entity_form.locations.breadcrumb_new') }
          ]}
          layoutMode="scrolling"
          actions={
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: '1rem' }} />}
              sx={{ px: 4, borderRadius: 2 }}
            >
              {t('entity_form.locations.save_location')}
            </Button>
          }
        >

          <Grid container spacing={4}>
            <Grid size={resolveResponsive({ xs: 12, lg: 8 })}>
              <Stack spacing={4}>
                <section id="main-info">
                  <BasicIdentitySection />
                </section>

                <section id="custom-fields">
                  {novelId && <CustomFieldsSection novelId={novelId} sections={locationSettings} />}
                </section>
              </Stack>
            </Grid>

            <Grid size={resolveResponsive({ xs: 12, lg: 4 })}>
              <Box component="aside" sx={{ position: { lg: 'sticky' }, top: 24 }}>
                <Stack spacing={4}>
                  <ImageManager
                    location={{ images: watchedImages, primaryImageId: watchedPrimaryImageId }}
                    pendingFiles={watchedPendingFiles}
                    isProcessing={isProcessing}
                    entityId={locationId}
                    visualDescription={watch('visualDescription')}
                    onVisualDescriptionChange={(value) => setValue('visualDescription', value, { shouldDirty: true })}
                    onUpdate={(updates) => {
                      if (updates.images) setValue('images', updates.images);
                      if (updates.pendingFiles) setValue('pendingFiles', updates.pendingFiles);
                      if (updates.primaryImageId !== undefined) setValue('primaryImageId', updates.primaryImageId);
                    }}
                  />

                  <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom>
                      {t('entity_form.locations.hierarchy_preview_title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {t('entity_form.locations.hierarchy_preview_desc')}
                    </Typography>

                    <Stack spacing={3}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        {selectedParent && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {selectedParent.name}
                            </Typography>
                            <ChevronRight sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {previewLocationName}
                        </Typography>
                      </Box>

                      <Autocomplete
                        fullWidth
                        options={locations}
                        value={selectedParent}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText={t('entity_form.locations.hierarchy_no_locations')}
                        onChange={(_event, parent) => {
                          setValue('parentId', parent?.id ?? null, { shouldDirty: true });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('entity_form.locations.hierarchy_parent_label')}
                            placeholder={t('entity_form.locations.hierarchy_parent_placeholder')}
                            size="small"
                          />
                        )}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isSaving}
          label={t('entity_form.locations.save_location')}
        />
      </form>
    </FormProvider>
  );
}

export default function NewLocationPage() {
  const { t } = useTranslation();
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
          <CircularProgress color="primary" thickness={2} size={48} />
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.2em' }}>
            {t('entity_form.locations.materializing')}
          </Typography>
        </Box>
      }>
        <NewLocationForm />
      </Suspense>
    </AuthGuard>
  );
}
